// Typed HTTP client implementing HMS_V4_API_CONTRACT.md.
//
// Everything the contract settles — list envelopes, error codes, ETag
// concurrency, branch headers — lives here rather than in feature code, so a
// screen never has to know about any of it. When the contract's deferred
// switches flip (envelope on by default, If-Match mandatory), this file
// changes and no screen does.

import {
  getAuthToken,
  getActiveBranchId,
  clearAuthSession,
  KIOSK_TOKEN,
} from './session';

const BASE = '/api';

// ── Contract §2: error codes ─────────────────────────────────────

/**
 * Machine-readable error codes. Switch on these — never on `message`, which
 * changes whenever someone fixes a typo, and never on HTTP status, which is
 * too coarse to distinguish "invalid input" from "rule forbids this".
 */
export type ApiErrorCode =
  | 'validation'
  | 'login'
  | 'forbidden'
  | 'not-found'
  | 'conflict'
  | 'conflict-stale'
  | 'business-rule'
  | 'exception';

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details?: Record<string, unknown>;

  constructor(message: string, code: ApiErrorCode, status: number, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  /** Field-level messages from a `validation` error, for form binding. */
  get fieldErrors(): Record<string, string> {
    if (this.code !== 'validation' || !this.details) return {};
    return Object.fromEntries(
      Object.entries(this.details).filter(([, v]) => typeof v === 'string')
    ) as Record<string, string>;
  }

  /** True when retrying the same request could plausibly succeed. */
  get isTransient(): boolean {
    return this.status >= 500;
  }
}

// Legacy routes predate the contract and return { error } with no code — the
// contract specifies treating a missing code as 'exception'. Doing that
// mapping here means feature code can assume `code` is always present.
function normalizeCode(raw: unknown, status: number): ApiErrorCode {
  const known: ApiErrorCode[] = [
    'validation', 'login', 'forbidden', 'not-found',
    'conflict', 'conflict-stale', 'business-rule', 'exception',
  ];
  if (typeof raw === 'string' && (known as string[]).includes(raw)) return raw as ApiErrorCode;
  switch (status) {
    case 400: return 'validation';
    case 401: return 'login';
    case 403: return 'forbidden';
    case 404: return 'not-found';
    case 409: return 'conflict';
    case 412: return 'conflict-stale';
    case 422: return 'business-rule';
    default: return 'exception';
  }
}

// ── Contract §1: list envelopes ──────────────────────────────────

export interface Paged<T> {
  data: T[];
  page: number;
  limit: number;
  /** null when the endpoint chose not to pay for a COUNT. */
  total: number | null;
  has_more: boolean;
}

/**
 * Accepts either response shape and returns the rows.
 *
 * Needed because paging is opt-in: an endpoint returns a bare array unless the
 * caller sends ?page=. Components should never branch on which shape came
 * back — call this and move on.
 */
export function unwrap<T>(result: T[] | Paged<T>): T[] {
  return Array.isArray(result) ? result : result.data;
}

export interface PageParams {
  page?: number;
  limit?: number;
}

// ── Transport ────────────────────────────────────────────────────

export interface RequestOptions {
  /** Sent as If-Match. Contract §3 — required on mutations by new screens. */
  ifMatch?: string;
  signal?: AbortSignal;
  /** Appended as query string; undefined/null entries are dropped. */
  query?: Record<string, string | number | boolean | null | undefined>;
}

/** A response plus the ETag needed to write against it later. */
export interface Versioned<T> {
  data: T;
  etag: string | null;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  if (!query) return `${BASE}${path}`;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${BASE}${path}?${qs}` : `${BASE}${path}`;
}

function buildHeaders(hasBody: boolean, ifMatch?: string): HeadersInit {
  const headers: Record<string, string> = {};
  if (hasBody) headers['Content-Type'] = 'application/json';

  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    // Contract §5: branch is client-supplied but server-authorized. Only sent
    // alongside a staff token — a kiosk's branch comes from its device token.
    const branchId = getActiveBranchId();
    if (branchId !== null) headers['X-Branch-Id'] = String(branchId);
  } else if (KIOSK_TOKEN) {
    headers['X-Kiosk-Token'] = KIOSK_TOKEN;
  }

  // NOTE: no x-user header. The legacy src/api.js sends it and legacy routes
  // still trust it for audit attribution, but it is client-supplied and
  // therefore spoofable. Routes written against this contract read the
  // verified JWT instead, so sending it would be misleading.
  if (ifMatch) headers['If-Match'] = ifMatch;

  return headers;
}

/**
 * Called on a 401 for a request we believed was authenticated — the session
 * expired or was revoked server-side. Kept as a hook rather than an inline
 * window.location.reload() so tests and the login screen can opt out.
 */
let onSessionExpired: () => void = () => {
  clearAuthSession();
  window.location.reload();
};

export function setSessionExpiredHandler(fn: () => void): void {
  onSessionExpired = fn;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: RequestOptions = {}
): Promise<Versioned<T>> {
  const res = await fetch(buildUrl(path, opts.query), {
    method,
    headers: buildHeaders(body !== undefined, opts.ifMatch),
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal: opts.signal,
  });

  if (!res.ok) {
    // A 401 while we held a token means the session died. A 401 from the login
    // endpoint itself is just a wrong password and must be shown inline, not
    // treated as a logout — same carve-out the legacy client makes.
    if (res.status === 401 && getAuthToken() && !path.startsWith('/auth/login')) {
      onSessionExpired();
    }
    const payload = (await res.json().catch(() => ({}))) as {
      error?: string;
      code?: string;
      details?: Record<string, unknown>;
    };
    throw new ApiError(
      payload.error || `Request failed (${res.status})`,
      normalizeCode(payload.code, res.status),
      res.status,
      payload.details
    );
  }

  // 204 and other empty bodies.
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return { data: undefined as T, etag: res.headers.get('etag') };
  }

  return { data: (await res.json()) as T, etag: res.headers.get('etag') };
}

// ── Public verbs ─────────────────────────────────────────────────
//
// Two flavours of each read: the plain one returns just the body, the
// `*Versioned` one also returns the ETag. Use the versioned form whenever the
// value will later be edited — that ETag is what makes the write safe.

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts).then((r) => r.data),
  getVersioned: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),

  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, body, opts).then((r) => r.data),
  postVersioned: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, body, opts),

  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', path, body, opts).then((r) => r.data),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PATCH', path, body, opts).then((r) => r.data),
  delete: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, undefined, opts).then((r) => r.data),

  /**
   * Fetches a list with the envelope explicitly requested (contract §1: new
   * code should always send ?page=). Returns the full envelope so callers get
   * `total` and `has_more` for pagination controls.
   */
  list: <T>(path: string, params: PageParams = {}, opts: RequestOptions = {}) =>
    request<Paged<T>>('GET', path, undefined, {
      ...opts,
      query: { ...opts.query, page: params.page ?? 1, limit: params.limit },
    }).then((r) => r.data),
};
